import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Checkbox, TextField, DialogTitle, DialogActions, Button, DialogContent, Dialog, InputAdornment, FormControl, FormGroup, FormControlLabel, InputLabel, MenuItem, Select, FormHelperText, OutlinedInput, Typography } from "@mui/material";

import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import ActivityCategoryFormDialog from "./ActivityCategoryFormDialog";
import CategoryIcon from "@mui/icons-material/Category";

import { DataTypes } from "../../../../constants";
import { preventDialogBackdropClick } from "../../../../utilities/generators";
import rmsActions from "../../../../actions/rmsActions";
import { getActivityTypes, getRmsAppCode, isFetching } from "../../../../reducers";


function ActivityFormDialog(props) {
    const dispatch = useDispatch();
    const appCode = useSelector(getRmsAppCode);
    const fetching = useSelector((state) => isFetching(state, DataTypes.ACTIVITY));
    let types = useSelector((state) => getActivityTypes(state));

    if (!fetching && types === null) {
        dispatch(rmsActions.getActivityTypes(appCode));
        types = [];
    }

    return (
        <Dialog
            open={!!props.open}
            onClose={preventDialogBackdropClick(props.onClose)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>{props.formTitle || "New activity"}</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth={true}
                        required={true}
                        error={!props?.type?.id}
                    >
                        <InputLabel id="label-type" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Activity type
                        </InputLabel>
                        <Select
                            id="select-type"
                            value={props.type.id || ""}
                            onChange={({target}) => props.setType(types.find(({ id }) => id === target.value))}
                        >
                            {types?.length > 0 &&
                                types.map((option) => (
                                    <MenuItem value={option.id} key={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>
                            Select the type of activity
                        </FormHelperText>
                    </FormControl>
                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth={true}
                        required={true}
                        error={!props.title}
                    >
                        <InputLabel id="label-title" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Activity
                        </InputLabel>
                        <OutlinedInput
                            id="text-title"
                            value={props.title}
                            onChange={({target}) => props.setTitle(target.value)}
                            inputProps={{ maxLength: 250 }}
                            multiline
                            rows={1}
                        />
                        <FormHelperText>Up to 250 characters</FormHelperText>
                    </FormControl>
                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth={true}
                        required={true}
                        error={!props.description}
                    >
                        <InputLabel id="label-description" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Description
                        </InputLabel>
                        <OutlinedInput
                            id="text-description"
                            placeholder="Add a description here..."
                            value={props.description}
                            onChange={({target}) => props.setDescription(target.value)}
                            multiline
                            rows={3}
                        />
                        <FormHelperText>Describe this activity</FormHelperText>
                    </FormControl>

                    <Box className="flex-container">
                        <FormControl component="div" margin="normal">
                            <DateTimePicker
                                id="date-start"
                                label="Start"
                                ampm={false}
                                format="DD/MM hh:ss"
                                value={!!props.dateStart ? dayjs(props.dateStart) : null}
                                onChange={props.setDateStart}
                                renderInput={(props) => <TextField {...props} />}
                            />
                            <FormHelperText>
                                Select a start date.
                            </FormHelperText>
                        </FormControl>

                        <FormControl component="div" margin="normal">
                            <DateTimePicker
                                id="date-end"
                                label="End"
                                ampm={false}
                                format="DD/MM hh:mm"
                                value={!!props.dateEnd ? dayjs(props.dateEnd) : null}
                                onChange={props.setDateEnd}
                                renderInput={(props) => <TextField {...props} />}
                            />
                            <FormHelperText>Select an end date.</FormHelperText>
                        </FormControl>
                    </Box>

                    <Box className="flex-container">
                        <FormControl
                            component="div"
                            margin="normal"
                            sx={{ width: "25ch" }}
                        >
                            <InputLabel id="duration-label" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                                Duration
                            </InputLabel>
                            <OutlinedInput
                                id="text-duration"
                                value={props.duration}
                                type="number"
                                inputProps={{
                                    min: 0,
                                    max: 1440,
                                    step: 1,
                                    inputMode: "numeric", // Only allow numeric input
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <Typography variant="caption">
                                            mins
                                        </Typography>
                                    </InputAdornment>
                                }
                                onChange={({target}) => props.setDuration(target.value)}
                            />
                            <FormHelperText>
                                If less than one day
                            </FormHelperText>
                        </FormControl>

                        <FormControl
                            component="div"
                            margin="normal"
                            sx={{ width: "25ch" }}
                            error={props?.score?.value === null || props?.score?.value === undefined}
                        >
                            <InputLabel id="score-label" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                                Value
                            </InputLabel>
                            <OutlinedInput
                                id="text-score"
                                value={props?.score?.value}
                                type="number"
                                inputProps={{
                                    min: 0,
                                    max: 150,
                                    step: 1,
                                    inputMode: "numeric", // Only allow numeric input
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <Typography variant="caption">
                                            pts
                                        </Typography>
                                    </InputAdornment>
                                }
                                onChange={({target}) => props.setScore({value: target.value, unit: ""})}
                            />
                            <FormHelperText>CPD points</FormHelperText>
                        </FormControl>
                        <Box className="form-row">
                            <FormHelperText>
                                Select one or more categories{" "}
                                <span className="error"> *</span>
                            </FormHelperText>
                            <Button
                                variant="contained"
                                color={!props.categories?.length ? "error" : "neutral"}
                                disableElevation={true}
                                startIcon={<CategoryIcon />}
                                key="upload"
                                onClick={() => props.setOpenCategoryDialog(true)}
                            >
                                Link to categories
                            </Button>
                        </Box>
                    </Box>

                    {!props.isEdit && (
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="review_na"
                                        checked={props.reviewNA}
                                        onChange={({target}) => props.setReviewNA(target.checked)}
                                    />
                                }
                                label="Reflection not required"
                            />
                        </FormGroup>
                    )}

                    {props.openCategoryDialog && (
                        <ActivityCategoryFormDialog
                            open
                            formTitle="Link to categories"
                            data={props.categories}
                            onClose={() => props.setOpenCategoryDialog(false)}
                            onSave={(params) => props.setCategories(params)}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={props.onClose}>
                    Cancel
                </Button>
                <Button
                    key="save"
                    variant="contained"
                    color="primary"
                    disableElevation={true}
                    onClick={props.onSubmit}
                    disabled={
                        !props.type ||
                        !props.title ||
                        !props.description ||
                        props.score.value === null ||
                        props.score.value === undefined ||
                        !props.categories?.length
                    }
                >
                    {props.submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ActivityFormDialog;
